import React from 'react';
import { Button } from '@hqo/ui-atoms';
import { Minus, Plus } from 'lucide-react';

export interface QuantityStepperProps {
  value: number;
  onIncrease: () => void;
  onDecrease: () => void;
  canDecrease: boolean;
  canIncrease: boolean;
  /* Accessible names for the two buttons, e.g. "Remove" / "Add". */
  decreaseLabel: string;
  increaseLabel: string;
  /* Id of the element naming the item, so each button reads as "Add · Cleaning service". */
  itemLabelId?: string;
  /* What the box shows, e.g. "2 units". The raw value when absent. */
  unitLabel?: string;
  testIdPrefix?: string;
}

/**
 * A quantity control: − · the current amount · +. Presentational only — the caller decides what each
 * press means and when either side is held, so the rules about a product's range stay with it.
 *
 * Both buttons are real `<button disabled>`: a held side cannot be pressed at all, rather than greying
 * its icon while the click stays bound. The decrease side is neutral; the increase side follows the
 * building's brand through `--primary`, as the interaction affordance on the row.
 *
 * The amount is display-only and announced politely, since both buttons keep their own names and the
 * number is the only thing a press changes.
 */
export const QuantityStepper = ({
  value,
  onIncrease,
  onDecrease,
  canDecrease,
  canIncrease,
  decreaseLabel,
  increaseLabel,
  itemLabelId,
  unitLabel,
  testIdPrefix = 'quantity-stepper',
}: QuantityStepperProps): JSX.Element => (
  <div className="flex items-center gap-2" data-testid={testIdPrefix}>
    <Button
      type="button"
      variant="outline"
      size="icon"
      className="size-8 shrink-0 rounded-full border-border text-foreground"
      onClick={onDecrease}
      disabled={!canDecrease}
      aria-label={decreaseLabel}
      aria-describedby={itemLabelId}
      data-testid={`${testIdPrefix}-decrease`}
    >
      <Minus className="size-4" aria-hidden="true" />
    </Button>
    <span
      className="flex h-8 min-w-16 items-center justify-center rounded-md border border-border px-3 text-sm text-foreground tabular-nums"
      aria-live="polite"
      data-testid={`${testIdPrefix}-value`}
    >
      {unitLabel ?? value}
    </span>
    <Button
      type="button"
      variant="outline"
      size="icon"
      className="size-8 shrink-0 rounded-full border-primary text-primary hover:bg-primary/10 hover:text-primary"
      onClick={onIncrease}
      disabled={!canIncrease}
      aria-label={increaseLabel}
      aria-describedby={itemLabelId}
      data-testid={`${testIdPrefix}-increase`}
    >
      <Plus className="size-4" aria-hidden="true" />
    </Button>
  </div>
);
